import React, { useState } from "react";
import "./ai-management.scss";

const PROMPT_CONFIGS = {
  parseNews: {
    actionKey: "parse_news",
    title: "Parse Crawled News",
    description:
      "AI reads raw crawled content and converts it into structured news records for the News page.",
    model: "gpt-4.1-mini",
    status: "Active",
    template: `You are an AI assistant for YK Channel.

Your task is to parse crawled construction news into structured project records.

Extract these fields:
- Issue
- Project Name
- State
- Category
- Stage
- Project Value
- Developer
- Main Consultant
- Main Contractor
- Construction Start
- Source
- Source URL
- Summary

Rules:
1. Do not invent missing data.
2. If a field is unknown, return "-".
3. Use RM format for project value when available.
4. Keep project names concise.
5. Return valid JSON only.`,
  },
  consolidateNews: {
    actionKey: "consolidate_news",
    title: "Consolidate Duplicate News",
    description:
      "AI compares crawled records from multiple sources and merges duplicate or similar project news.",
    model: "gpt-4.1-mini",
    status: "Active",
    template: `You are an AI assistant for YK Channel.

Your task is to detect duplicate or similar construction project news from multiple sources.

Compare records using:
- Project Name similarity
- Developer
- Location / State
- Category
- Construction Stage
- Project Value
- Consultant / Contractor names

Rules:
1. Merge records that refer to the same construction project.
2. Preserve the most complete and reliable information.
3. Keep all source references.
4. Mark confidence score from 0 to 100.
5. Explain why records were merged.
6. Return valid JSON only.`,
  },
};

const tokenUsage = [
  8200, 11600, 9800, 15400, 13200, 17600, 14400, 19000, 21600, 18200,
  24000, 22600, 25800, 20100, 27600, 29400, 24700, 31000, 33600, 28900,
  36500, 34200, 38800, 35700, 42100, 39700, 44200, 46800, 43000, 49200,
];

const models = ["gpt-4.1-mini", "gpt-4.1", "gpt-4o-mini", "gpt-4o"];
const statuses = ["Active", "Inactive"];
export default function AIManagementPage() {
  const [credentials, setCredentials] = useState({
    provider: "OpenAI",
    apiKey: "",
    connectionStatus: "Pending",
    lastTestedAt: "Not tested yet",
  });

  const [prompts, setPrompts] = useState(PROMPT_CONFIGS);
  const [testResult, setTestResult] = useState("");

  const maxUsage = Math.max(...tokenUsage);
  const totalTokens = tokenUsage.reduce((total, value) => total + value, 0);
  const avgTokens = Math.round(totalTokens / tokenUsage.length);

  const handleCredentialChange = (key, value) => {
    setCredentials((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePromptChange = (section, key, value) => {
    setPrompts((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleTestConnection = () => {
    const now = new Date().toLocaleString();

    setCredentials((prev) => ({
      ...prev,
      connectionStatus: "Verified",
      lastTestedAt: now,
    }));

    setTestResult(
      JSON.stringify(
        {
          provider: credentials.provider,
          connection_status: "Verified",
          last_tested_at: now,
          message: "Connection test completed. Replace this mock result with your backend OpenAI test endpoint.",
        },
        null,
        2
      )
    );
  };

  const handleSaveCredentials = () => {
    console.log("Saving OpenAI credentials:", {
      ...credentials,
      apiKey: credentials.apiKey ? "********" : "",
    });

    alert("OpenAI credential settings saved.");
  };

  const handleSavePrompt = (section) => {
    console.log("Saving AI prompt:", prompts[section]);
    alert(`${prompts[section].title} prompt saved.`);
  };

  const handleTestPrompt = (section) => {
    const prompt = prompts[section];

    setTestResult(
      JSON.stringify(
        {
          action_key: prompt.actionKey,
          model: prompt.model,
          status: prompt.status,
          provider: credentials.provider,
          message:
            "Test request prepared successfully. Connect this button to your backend OpenAI endpoint.",
        },
        null,
        2
      )
    );
  };

  const renderPromptCard = (sectionKey) => {
    const item = prompts[sectionKey];

    return (
      <div className="ai-card ai-prompt-card" key={sectionKey}>
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">{item.actionKey}</span>
            <h2>{item.title}</h2>
            <p>{item.description}</p>
          </div>

          <span className={`ai-status ai-status--${item.status.toLowerCase()}`}>
            {item.status}
          </span>
        </div>

        <div className="ai-form-grid ai-form-grid--two">
          <label>
            Model
            <select
              value={item.model}
              onChange={(event) =>
                handlePromptChange(sectionKey, "model", event.target.value)
              }
            >
              {models.map((model) => (
                <option key={model}>{model}</option>
              ))}
            </select>
          </label>

          <label>
            Status
            <select
              value={item.status}
              onChange={(event) =>
                handlePromptChange(sectionKey, "status", event.target.value)
              }
            >
              {statuses.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="ai-textarea-label">
          Prompt Template
          <textarea
            value={item.template}
            onChange={(event) =>
              handlePromptChange(sectionKey, "template", event.target.value)
            }
          />
        </label>

        <div className="ai-card__actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => handleTestPrompt(sectionKey)}
          >
            Test Prompt
          </button>

          <button
            type="button"
            className="primary-button"
            onClick={() => handleSavePrompt(sectionKey)}
          >
            Save Prompt
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="ai-management-page">
      <div className="ai-management-page__header">
        <div>
          <h1>AI Management</h1>
          <p>
            Manage OpenAI credentials, monitor token usage, and configure prompts for news parsing and duplicate consolidation.
          </p>
        </div>
      </div>

      <div className="ai-card usage-card">
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">usage</span>
            <h2>Daily Token Usage</h2>
            <p>Last 30 days OpenAI token consumption.</p>
          </div>
        </div>

        <div className="usage-summary">
          <div>
            <label>Total Tokens</label>
            <strong>{totalTokens.toLocaleString()}</strong>
          </div>
          <div>
            <label>Daily Average</label>
            <strong>{avgTokens.toLocaleString()}</strong>
          </div>
          <div>
            <label>Highest Day</label>
            <strong>{maxUsage.toLocaleString()}</strong>
          </div>
        </div>

        <div className="usage-chart" aria-label="Daily token usage chart">
          {tokenUsage.map((value, index) => (
            <div className="usage-chart__bar-wrap" key={index}>
              <div
                className="usage-chart__bar"
                style={{ height: `${Math.max(12, (value / maxUsage) * 100)}%` }}
                title={`Day ${index + 1}: ${value.toLocaleString()} tokens`}
              />
              {(index === 0 || index === 14 || index === 29) && (
                <span>{index + 1}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="ai-card">
        <div className="ai-card__header">
          <div>
            <span className="ai-badge">credential</span>
            <h2>OpenAI Credential Settings</h2>
            <p>
              Configure the OpenAI API credential used by crawler parsing and duplicate consolidation jobs.
            </p>
          </div>

          <span
            className={`ai-status ai-status--connection-${credentials.connectionStatus.toLowerCase()}`}
          >
            {credentials.connectionStatus}
          </span>
        </div>

        <div className="connection-meta connection-meta--two">
          <div>
            <label>Connection Status</label>
            <strong>{credentials.connectionStatus}</strong>
          </div>
          <div>
            <label>Last Tested Time</label>
            <strong>{credentials.lastTestedAt}</strong>
          </div>
        </div>

        <div className="ai-form-grid ai-form-grid--credential">
          <label>
            Provider
            <input value={credentials.provider} disabled />
          </label>

          <label>
            Connection Status
            <input value={credentials.connectionStatus} disabled />
          </label>

          <label className="ai-form-grid__wide">
            API Key
            <input
              type="password"
              placeholder="sk-..."
              value={credentials.apiKey}
              onChange={(event) =>
                handleCredentialChange("apiKey", event.target.value)
              }
            />
          </label>
        </div>

        <div className="ai-card__actions">
          <button
            type="button"
            className="secondary-button"
            onClick={handleTestConnection}
          >
            Test Connection
          </button>

          <button
            type="button"
            className="primary-button"
            onClick={handleSaveCredentials}
          >
            Save Credential
          </button>
        </div>
      </div>

      {renderPromptCard("parseNews")}
      {renderPromptCard("consolidateNews")}

      {testResult && (
        <div className="ai-test-result">
          <div className="ai-test-result__header">
            <h3>Test Result</h3>
            <button type="button" onClick={() => setTestResult("")}>
              Clear
            </button>
          </div>

          <pre>{testResult}</pre>
        </div>
      )}
    </div>
  );
}
